import { t } from '@lingui/core/macro';
import { Plural } from '@lingui/react/macro';
import { useMemo } from 'react';

import {
  NumberInput,
  NumberInputGroup,
  NumberInputInput,
  NumberInputTrigger,
  withFieldGroup,
} from '@/components/Form';
import { SelectContent, SelectItem, SelectValue } from '@/components/Form/pickers';
import { cn } from '@/lib/utils';

import RuleBuilderText from './RuleBuilderText';
import type { AggregateFieldValues } from './types';

const RuleBuilderAggregateAttribute = withFieldGroup({
  defaultValues: {
    operator: 'gte',
    value: [],
  } as AggregateFieldValues,
  props: {
    className: undefined as string | undefined,
    disabled: false,
  },
  render: function RuleBuilderAggregateAttributeRender({ group, className, disabled = false }) {
    const countOptions = useMemo(
      () => [
        { label: t`at least`, value: 'gte' },
        { label: t`at most`, value: 'lte' },
        { label: t`exactly`, value: 'eq' },
        { label: t`more than`, value: 'gt' },
        { label: t`less than`, value: 'lt' },
      ],
      [],
    );

    return (
      <div className={cn(className, 'flex items-start justify-start gap-6')}>
        <group.AppField name="operator">
          {(field) => (
            <field.Field>
              <field.Select items={countOptions} disabled={disabled}>
                <field.SelectTrigger className="w-40">
                  <SelectValue placeholder={t`Select one...`} />
                </field.SelectTrigger>
                <SelectContent>
                  {countOptions.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </field.Select>
              <field.Error />
            </field.Field>
          )}
        </group.AppField>
        <group.AppField name="value">
          {(field) => (
            <field.Field>
              <NumberInput
                style={{ width: 85 }}
                min={0}
                max={99999}
                value={field.state.value[0]}
                disabled={disabled}
                format={{ useGrouping: true, maximumFractionDigits: 0 }}
                onValueChange={(value) => field.handleChange(value == null ? [] : [value])}
              >
                <NumberInputGroup>
                  <NumberInputInput data-testid="aggregate-count-input" onBlur={field.handleBlur} />
                  <NumberInputTrigger />
                </NumberInputGroup>
              </NumberInput>
              <field.Error />
            </field.Field>
          )}
        </group.AppField>
        <group.Subscribe selector={(state) => state.values.value}>
          {(aggregateValue) => (
            <RuleBuilderText variant="body1">
              <Plural value={aggregateValue[0] ?? 0} one="time" other="times" />
            </RuleBuilderText>
          )}
        </group.Subscribe>
      </div>
    );
  },
});

export default RuleBuilderAggregateAttribute;
